import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { paymentService, usageService } from '../services/api';
import logoDark from '../assets/logo.png';
import logoLight from '../assets/logo-dark.png';

function TopUpRequired() {
  const [topUpAmount, setTopUpAmount] = useState(10);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [usePromoCode, setUsePromoCode] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { currentUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();

  // Initialize Stripe with publishable key
  const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const profile = await usageService.getUserProfile();
        console.log('Fetched profile:', profile);
        setUserProfile(profile);
        
        // If user has already made a top-up, redirect to dashboard
        if (profile && profile.has_made_top_up) {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        notify.error('Failed to load user profile');
      } finally {
        // Always set loading to false, even if there's an error
        setLoading(false);
      }
    };
    
    // Only fetch if we have a current user
    if (currentUser) {
      fetchUserProfile();
    } else {
      // If no current user, still set loading to false
      setLoading(false);
    }
  }, [navigate, notify, currentUser]);

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  // Process payment
  const processPayment = async () => {
    try {
      setProcessingPayment(true);
      
      // Call payment API to get client_secret or process promo code
      const response = usePromoCode
        ? await paymentService.processPayment(null, promoCode)
        : await paymentService.processPayment(topUpAmount);
      
      // Handle promo code response
      if (usePromoCode && response) {
        // Always reset processing state for promo code submissions
        setProcessingPayment(false);
        
        // Check if promo code was successfully applied
        if (response.promo_applied === true && response.transaction_id) {
          // Get promo amount, handling different possible formats
          const promoAmount = response.promo_amount || 0;
          const formattedAmount = promoAmount > 100 ? promoAmount / 100 : promoAmount;
          
          // Promo code was successfully applied - show success message
          notify.success(`Promo code applied! ${formatCurrency(formattedAmount)} has been added to your account.`);
          
          // Reset form state
          setPromoCode('');
          
          // Immediately redirect to dashboard after successful promo code application
          window.location.href = '/dashboard';
        } else if (response.error_message || response.error_type || response.promo_applied === false) {
          // Use generic error message for all promo code errors, like Usage page
          notify.error('Failed to process payment. Please try again.');
          // Reset promo code on error
          setPromoCode('');
          // Do not redirect on failure - stay on the page so user can try again
        }
        
        return;
      }
      
      // Check for payment error response - use generic message like Usage page
      if (response && !usePromoCode && (response.error_message || response.error_type)) {
        // Handle payment error with generic message
        setProcessingPayment(false);
        notify.error('Failed to process payment. Please try again.');
        return;
      }
      
      // Handle regular payment with Stripe
      if (response && response.client_secret) {
        // Load Stripe.js
        const stripe = await stripePromise;
        
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        // Create a payment element and mount it to the DOM
        const elements = stripe.elements({
          clientSecret: response.client_secret,
          appearance: {
            theme: 'stripe',
          },
        });
        
        // Create a container for Stripe Elements if it doesn't exist
        let container = document.getElementById('stripe-payment-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'stripe-payment-container';
          container.style.position = 'fixed';
          container.style.zIndex = '9999';
          container.style.left = '0';
          container.style.top = '0';
          container.style.width = '100%';
          container.style.height = '100%';
          container.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          container.style.display = 'flex';
          container.style.justifyContent = 'center';
          container.style.alignItems = 'center';
          document.body.appendChild(container);
        }
        
        // Create the inner container for the payment form
        const paymentContainer = document.createElement('div');
        paymentContainer.style.backgroundColor = 'white';
        paymentContainer.style.padding = '24px';
        paymentContainer.style.borderRadius = '8px';
        paymentContainer.style.width = '450px';
        paymentContainer.style.maxWidth = '100%';
        paymentContainer.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
        container.appendChild(paymentContainer);
        
        // Create header
        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.alignItems = 'center';
        header.style.marginBottom = '20px';
        paymentContainer.appendChild(header);
        
        // Add title
        const title = document.createElement('h3');
        title.textContent = 'Complete Payment';
        title.style.margin = '0';
        title.style.fontSize = '18px';
        title.style.fontWeight = '600';
        header.appendChild(title);
        
        // Add close button
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '&times;';
        closeButton.style.background = 'none';
        closeButton.style.border = 'none';
        closeButton.style.fontSize = '24px';
        closeButton.style.cursor = 'pointer';
        closeButton.style.padding = '0';
        closeButton.style.lineHeight = '1';
        header.appendChild(closeButton);
        
        // Create payment element
        const paymentElement = elements.create('payment');
        paymentElement.mount(paymentContainer);
        
        // Create pay button
        const payButton = document.createElement('button');
        payButton.textContent = 'Pay Now';
        payButton.style.marginTop = '20px';
        payButton.style.width = '100%';
        payButton.style.padding = '10px';
        payButton.style.backgroundColor = '#4F46E5';
        payButton.style.color = 'white';
        payButton.style.border = 'none';
        payButton.style.borderRadius = '4px';
        payButton.style.fontSize = '16px';
        payButton.style.cursor = 'pointer';
        paymentContainer.appendChild(payButton);
        
        // Create cancel button
        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'Cancel';
        cancelButton.style.marginTop = '10px';
        cancelButton.style.width = '100%';
        cancelButton.style.padding = '10px';
        cancelButton.style.backgroundColor = 'transparent';
        cancelButton.style.color = '#6B7280';
        cancelButton.style.border = '1px solid #D1D5DB';
        cancelButton.style.borderRadius = '4px';
        cancelButton.style.fontSize = '16px';
        cancelButton.style.cursor = 'pointer';
        paymentContainer.appendChild(cancelButton);
        
        // Handle pay button click
        payButton.addEventListener('click', async () => {
          payButton.disabled = true;
          payButton.innerHTML = '<span class="spinner"></span> Processing...';
          payButton.style.opacity = '0.7';
          
          // Add a spinner style
          const style = document.createElement('style');
          style.textContent = `
            .spinner {
              display: inline-block;
              width: 16px;
              height: 16px;
              border: 2px solid rgba(255,255,255,0.3);
              border-radius: 50%;
              border-top-color: white;
              animation: spin 1s ease-in-out infinite;
              margin-right: 8px;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `;
          document.head.appendChild(style);
          
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
          });
          
          if (error) {
            // Payment failed but keep the modal open
            payButton.disabled = false;
            payButton.innerHTML = 'Try Again';
            payButton.style.opacity = '1';
            
            // Create an error message element if it doesn't exist
            let errorElement = document.getElementById('payment-error-message');
            if (!errorElement) {
              errorElement = document.createElement('div');
              errorElement.id = 'payment-error-message';
              errorElement.style.color = '#e53e3e';
              errorElement.style.backgroundColor = '#fed7d7';
              errorElement.style.padding = '12px';
              errorElement.style.borderRadius = '4px';
              errorElement.style.marginBottom = '16px';
              errorElement.style.fontSize = '14px';
              
              // Insert before the payment element container
              paymentContainer.parentNode.insertBefore(errorElement, paymentContainer);
            }
            
            // Update the error message
            errorElement.textContent = error.message || 'Payment failed. Please try again.';
            
            // Scroll to the error message
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            // Payment succeeded - show success message and clean up
            document.body.removeChild(container);
            setProcessingPayment(false);
            
            // Show success message and immediately navigate to dashboard
            notify.success('Payment completed successfully!');
            
            // Immediately redirect on successful payment
            window.location.href = '/dashboard';
          }
        });
        
        // Handle cancel
        closeButton.addEventListener('click', () => {
          document.body.removeChild(container);
          setProcessingPayment(false);
        });
        
        // Handle cancel button
        cancelButton.addEventListener('click', () => {
          document.body.removeChild(container);
          setProcessingPayment(false);
        });
        
        // Allow users to update card details and try again
        elements.on('change', (event) => {
          // Clear error message when user updates their details
          const errorElement = document.getElementById('payment-error-message');
          if (errorElement && event.complete) {
            errorElement.style.display = 'none';
          }
        });
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      notify.error('Failed to process payment. Please try again.');
      setProcessingPayment(false);
      setPromoCode('');
      if (usePromoCode) {
        setUsePromoCode(false);
      }
    }
  };

  // Show spinner only during initial load
  const showSpinner = loading && !userProfile;
  
  // If we're still loading and have no profile data, show a spinner
  if (showSpinner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <svg className="animate-spin h-10 w-10 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img src={darkMode ? logoLight : logoDark} alt="Plexe Logo" className="h-16 w-16 mb-6" />
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Complete Your Account Setup
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Add credits to your account to start using our services
          </p>
          <button 
            onClick={toggleDarkMode} 
            className="mt-4 p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 
                     dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-all duration-200
                     transform hover:scale-105 hover:shadow-md"
          >
            {darkMode ? 
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
              </svg> 
              : 
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
              </svg>
            }
          </button>
        </div>
        
        <div className="mt-8">
          
          {!usePromoCode ? (
            <div className="rounded-md shadow-sm">
              <div className="mb-4">
                <label htmlFor="amount" className="sr-only">Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400">$</span>
                  </div>
                  <input
                    type="text"
                    id="amount"
                    value={topUpAmount}
                    onChange={(e) => {
                      // Allow direct editing of the field
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      if (value === '') {
                        setTopUpAmount('');
                      } else {
                        const numValue = Number(value);
                        if (!isNaN(numValue)) {
                          setTopUpAmount(value);
                        }
                      }
                    }}
                    onBlur={() => {
                      // Only enforce minimum on blur
                      if (topUpAmount === '' || Number(topUpAmount) < 10) {
                        setTopUpAmount(10);
                      }
                    }}
                    className="pl-7 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-gray-400"
                    placeholder="Amount (minimum $10)"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400">USD</span>
                  </div>
                </div>
                <button
                  onClick={() => setUsePromoCode(true)}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 text-left"
                >
                  I have a promo code
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-md shadow-sm">
              <div className="mb-4">
                <label htmlFor="promoCode" className="sr-only">Promo Code</label>
                <input
                  type="text"
                  id="promoCode"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter your promo code"
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-gray-400"
                />
                <button
                  onClick={() => setUsePromoCode(false)}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 text-left"
                >
                  Back to amount
                </button>
              </div>
            </div>
          )}
          
          <div>
            <button
              onClick={processPayment}
              disabled={processingPayment || (usePromoCode ? !promoCode.trim() : Number(topUpAmount) < 10)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingPayment ? (
                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  Processing...
                </>
              ) : (
                usePromoCode ? 'Apply Promo Code' : 'Proceed to Payment'
              )}
            </button>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export default TopUpRequired;
